/* eslint-disable import/order */
/* eslint-disable import/newline-after-import */
/* eslint-disable import/first */
/* global rawData, serverParams */
import * as d3 from 'd3'
import LittleMan, {
    cardSVGHeight,
    cardSVGWidth,
} from 'bam-node-baseball-savant/visuals/little-man'
import debounce from 'lodash/debounce'
import { GAME_TYPE_NAMES } from 'bam-node-baseball-savant/constants/game-types'
import { normalizeAccents } from 'bam-node-baseball-savant/utils/normalize-accents'
import createToggle from 'bam-node-baseball-savant/visuals/toggle'
import getShortPitchName from 'bam-node-baseball-savant/utils/get-short-pitch-name.ts'

import { guff } from 'bam-node-baseball-savant/utils/guff'
import { getPitchTypeColor } from 'bam-node-baseball-savant/utils/pitch-type-colors'
import {
    getMultiSelectValues,
    gameTypeModal,
    groupByModal,
    pitchTypeModal,
    seasonModal,
    teamModal,
} from '../utils/multi-select'
import loadPitcherArmAngleTable, { $tableDiv } from './pitcher-arm-angles/table'

let showLeagueAverage = false

const isGroupByPitchType = serverParams.secondaryGroupBy.includes(
    'api_pitch_type_group03'
)

const wait = (amount = 0) =>
    // eslint-disable-next-line no-promise-executor-return
    new Promise((resolve) => setTimeout(resolve, amount))

const $title = document.getElementById('chart-title')

const handSelectedTooltips = rawData.reduce((acc, curr) => {
    if (!curr.image_name) {
        return acc
    }
    acc[curr.pitcher] = curr.image_name
    return acc
}, {})

function getImageUrl(fileName) {
    return `https://builds.mlbstatic.com/baseballsavant.mlb.com/pitrelease/main/${fileName}`
}

const isWide = window.innerWidth >= 1100
const handSelectedKeys = isWide
    ? Object.keys(handSelectedTooltips).map(Number)
    : []

let centered = false

const allData = rawData.map((d) => {
    const rise = d.release_ball_z - d.shoulder_z
    // swapped x axis
    const run = d.relative_shoulder_x - d.relative_release_ball_x
    const radians = Math.atan2(rise, run)

    // hacky
    const [last, first] = d.name.split(', ')
    const name_display_first_last = `${first} ${last}`
    const short_name = `${first.slice(0, 1)}. ${last}`

    return {
        ...d,
        radians,
        degrees: (radians * 180) / Math.PI,
        rise,
        run,
        normal_name_display_last_first: normalizeAccents(d.name),
        name_display_first_last,
        normal_name_display_first_last: normalizeAccents(
            name_display_first_last
        ),
        short_name,
        normal_short_name: normalizeAccents(short_name),
    }
})

// We'll include at least this much
const vizLowerBound = 300
const thresholdArr = allData
    .sort((a, b) => (a.n_pitches > b.n_pitches ? -1 : 1))
    .slice(0, vizLowerBound)

const threshold = thresholdArr[thresholdArr.length - 1]?.n_pitches || 100

let data = allData
    .filter((d) => d.n_pitches >= threshold)
    .sort((a, b) => {
        const selectedA = handSelectedKeys.includes(a.pitcher)
        const selectedB = handSelectedKeys.includes(b.pitcher)

        if (selectedA !== selectedB) {
            return selectedA ? 1 : -1
        }
        return a.n_pitches > b.n_pitches ? -1 : 1
    })

const truncated = allData.length > data.length

const groupedData = data.reduce((acc, curr) => {
    if (!acc[curr.pitcher]) {
        acc[curr.pitcher] = {
            api_pitch_type: 'All', // This is wrong...
            name_display_last_first: curr.name,
            last_team_id: curr.last_team_id,
            pitch_hand: curr.pitch_hand,
            pitcher: curr.pitcher,
            short_name: curr.short_name,
            year: curr.year,
        }
        acc[curr.pitcher].angles = []
    }

    acc[curr.pitcher].angles.push({
        arm_angle: curr.arm_angle,
        year: curr.year,
        api_pitch_type: curr.api_pitch_type,
        pitch_hand: curr.pitch_hand,
        n_pitches: curr.n_pitches,
    })
    return acc
}, {})

// Supplement groupedData
Object.entries(groupedData).forEach(([playerId, { angles }]) => {
    // Because of rounding, this will not be perfect, that's okay.
    const n_pitches = d3.sum(angles, (d) => d.n_pitches)
    const arm_angle =
        d3.sum(angles, (d) => d.n_pitches * d.arm_angle) / n_pitches

    // Overload this property
    groupedData[playerId].arm_angle = arm_angle
    groupedData[playerId].n_pitches = n_pitches
})
const vizData = Object.values(groupedData)

let filteredData = data
let filteredVizData = vizData
let currentData
let filteredDataJson = JSON.stringify(filteredData)

function getSvgElementId(row, suffix = '') {
    return `little-man-${row.unique_id}-${suffix}`
}

const lilGuys = {}

// Only if isSplit = false
let isStacked = window.innerWidth < 540

const $wrap = d3.select('#arm-angle-minis')

const $wrapLefty = $wrap
    .append('div')
    .style('width', isStacked ? '100%' : '50%')

$wrapLefty
    .append('h3')
    .text('Lefties')
    .style('text-align', 'center')
    .style('margin-bottom', '20px')

const $wrapLeftyWrap = $wrapLefty.append('div').style('position', 'relative')

const $wrapRighty = $wrap
    .append('div')
    .style('width', isStacked ? '100%' : '50%')

$wrapRighty
    .append('h3')
    .text('Righties')
    .style('text-align', 'center')
    .style('margin-bottom', '20px')
const $wrapRightyWrap = $wrapRighty.append('div').style('position', 'relative')

const $wrapAll = $wrap
    .append('div')
    .style('width', '100%')
    .style('position', 'relative')

const svgClass = 'pitcher-arm-angle-mini'

function getNormalizedAngle({ pitch_hand, arm_angle }) {
    return pitch_hand === 'R' ? 180 - arm_angle : arm_angle
}

const rainbow_lr = (a, b) => {
    const angleA = getNormalizedAngle(a)
    const angleB = getNormalizedAngle(b)

    if (angleA !== angleB) {
        return angleA < angleB ? -1 : 1
    }

    // tiebreaker
    return 0
}
const rainbow_rl = (a, b) => {
    const angleA = getNormalizedAngle(a)
    const angleB = getNormalizedAngle(b)

    if (angleA !== angleB) {
        return angleA < angleB ? 1 : -1
    }

    // tiebreaker
    return 0
}
const descending = (a, b) => {
    if (a.arm_angle !== b.arm_angle) {
        return a.arm_angle > b.arm_angle ? -1 : 1
    }

    // tiebreaker
    return 0
}
const descending_hand = (a, b) => {
    if (a.pitch_hand !== b.pitch_hand) {
        return a.pitch_hand === 'R' ? 1 : -1
    }

    if (a.arm_angle !== b.arm_angle) {
        return a.arm_angle > b.arm_angle ? -1 : 1
    }

    // tiebreaker
    return 0
}
const ascending = (a, b) => {
    if (a.arm_angle !== b.arm_angle) {
        return a.arm_angle > b.arm_angle ? 1 : -1
    }

    // tiebreaker
    return 0
}
const ascending_hand = (a, b) => {
    if (a.pitch_hand !== b.pitch_hand) {
        return a.pitch_hand === 'R' ? 1 : -1
    }

    if (a.arm_angle !== b.arm_angle) {
        return a.arm_angle > b.arm_angle ? 1 : -1
    }

    // tiebreaker
    return 0
}

const sortingFns = {
    rainbow_lr,
    combo_rainbow_lr: rainbow_lr,
    rainbow_rl,
    combo_rainbow_rl: rainbow_rl,
    descending,
    combo_descending: descending,
    descending_hand,
    combo_descending_hand: descending_hand,
    ascending,
    combo_ascending: ascending,
    ascending_hand,
    combo_ascending_hand: ascending_hand,
}

const currentParams = new URLSearchParams(window.location.search)
const rawSortParam = currentParams.get('sort')
const sortParam = Object.keys(sortingFns).includes(rawSortParam)
    ? rawSortParam
    : 'ascending'

const rawSizeParam = currentParams.get('size')
let sizeParam = rawSizeParam || 'small'
const rawPerspectiveParam = currentParams.get('perspective')
let perspectiveParam = rawPerspectiveParam || 'back'

let isSplit = !sortParam.includes('combo_')

const initialSortFn = sortingFns[sortParam]
allData.sort(initialSortFn)
data.sort(initialSortFn)
vizData.sort(initialSortFn)

function getTeamLogoX(row) {
    const isBack = perspectiveParam !== 'front'
    const imageX =
        (row.pitch_hand === 'L' && isBack) ||
        (row.pitch_hand === 'R' && !isBack)
            ? 10
            : cardSVGWidth - 30

    return imageX
}
const DOT = '⋅'

// Return an array of up to two strings
function manageSubTitles(row) {
    const handedness = `${row.pitch_hand}HP`

    // Team is handled by the logo in the corner
    const groupBys = serverParams.secondaryGroupBy.filter(
        (d) => d !== 'fld_team_id'
    )

    // Try hard to show years
    if (groupBys.length === 0) {
        const [minYear, maxYear] = d3.extent(serverParams.validSeasons)

        // One Year Selected
        if (serverParams.validSeasons.length === 1) {
            return [`${handedness} ${DOT} ${serverParams.validSeasons[0]}`, '']
        }

        // Contiguous Years
        if (maxYear - minYear === serverParams.validSeasons.length - 1) {
            return [`${handedness} ${DOT} ${minYear} - ${maxYear}`, '']
        }

        if (serverParams.validSeasons.length <= 3) {
            return [
                `${handedness} ${DOT} ${serverParams.validSeasons.join(', ')}`,
                '',
            ]
        }
        return [handedness, '']
    }

    const firstGroupBys = groupBys.slice(0, 2)
    const secondGroupBys = groupBys.slice(2)

    const sub1 = getGroupByVals(row, firstGroupBys)
    const sub2 = getGroupByVals(row, secondGroupBys)

    return [[handedness, ...sub1].join(` ${DOT} `), sub2.join(` ${DOT} `)]
}

function createVisual(row, suffix = '') {
    const svgId = getSvgElementId(row, suffix)
    const $el = document.getElementById(svgId)

    const svg = d3.select($el)

    const vertPush = serverParams.secondaryGroupBy?.length > 2 ? 8 : 0
    const gWrap = svg.append('g').attr('transform', `translate(0, ${vertPush})`)
    const g = gWrap.append('g')

    // Visual Halfway point, debug alignment
    // const line = d3.line()

    // svg.append('path')
    //     .attr(
    //         'd',
    //         line([
    //             [cardSVGWidth / 2, 0],
    //             [cardSVGWidth / 2, cardSVGHeight],
    //         ])
    //     )
    //     .attr('stroke', 'green')
    //     .attr('fill', 'red')

    svg.append('a')
        .attr(
            'href',
            `/savant-player/${row.pitcher}?stats=statcast-r-pitching-mlb#arm_angles`
        )
        .attr('target', '_BLANK')
        .append('text')
        .text(row.short_name)
        .attr('x', cardSVGWidth / 2)
        .attr('y', 18)
        .attr('title', row.name)
        .attr('font-size', row.short_name.length > 17 ? 11 : 12)
        .attr('font-weight', 'bold')
        .attr('text-anchor', 'middle')

    const [sub1, sub2] = manageSubTitles(row)

    svg.append('text')
        .text(sub1)
        .attr('x', cardSVGWidth / 2)
        .attr('y', 32)
        .attr('font-size', 10)
        .attr('text-anchor', 'middle')

    svg.append('text')
        .text(sub2)
        .attr('x', cardSVGWidth / 2)
        .attr('y', 44)
        .attr('font-size', 10)
        .attr('text-anchor', 'middle')

    const isBack = perspectiveParam !== 'front'

    const defaultSavantUrl = `/site-core/images/savant-mlb.svg`
    const teamUrl = `https://midfield.mlbstatic.com/v1/team/${row.team_id}/spots/120`
    gWrap
        .append('image')
        .classed('mlb-team-logo', true)
        .attr('href', row.n_teams === 1 ? teamUrl : defaultSavantUrl)
        .attr('width', 20)
        .attr('x', getTeamLogoX)
        .attr('y', row.n_teams === 1 ? cardSVGHeight - 33 : cardSVGHeight - 25)

    const lilGuy = new LittleMan(g)

    lilGuys[svgId] = lilGuy

    lilGuy.update([row], isBack)
}
function getDims($wrapper) {
    let sizeMult = 1
    if (sizeParam === 'medium') {
        sizeMult = 2
    } else if (sizeParam === 'large') {
        sizeMult = 4
    }

    const wrapperDisplay = $wrapper.style('display')

    // Hacky, if display none all dimension values will be 0
    if (wrapperDisplay === 'none') {
        $wrapper.style('visibility', 'hidden').style('display', 'block')
    }
    const wrapperDims = $wrapper.node().getBoundingClientRect()

    if (wrapperDisplay === 'none') {
        $wrapper.style('visibility', 'visible').style('display', 'none')
    }

    const cols = Math.max(
        1,
        Math.floor(wrapperDims.width / (cardSVGWidth * sizeMult))
    )
    const cardMargin = 4

    const colWidth = wrapperDims.width / cols
    const cardWidth = Math.floor(colWidth - cardMargin)

    const multiplier = cardWidth / cardSVGWidth
    const cardHeight = multiplier * cardSVGHeight

    const rowHeight = cardHeight + cardMargin

    return {
        wrapperDims,
        cols,
        cardMargin,
        colWidth,
        cardWidth,
        multiplier,
        cardHeight,
        rowHeight,
    }
}

let halfDims = getDims($wrapLefty)
let wholeDims = getDims($wrapAll)

let prevWidth = window.innerWidth

window.addEventListener(
    'resize',
    debounce(() => {
        // Really mostly for my own dev tools sanity.
        if (window.innerWidth === prevWidth) {
            return
        }
        prevWidth = window.innerWidth

        isStacked = window.innerWidth < 540
        $wrapLefty.style('width', isStacked ? '100%' : '50%')
        $wrapRighty.style('width', isStacked ? '100%' : '50%')

        halfDims = getDims($wrapLefty)
        wholeDims = getDims($wrapAll)

        updateGrid(filteredData)
    }, 200)
)

// eslint-disable-next-line consistent-return
function updateGrid(sortedData, isAnimation = false) {
    // Split Data into two
    const lefties = sortedData.filter((d) => d.pitch_hand === 'L')
    const righties = sortedData.filter((d) => d.pitch_hand !== 'L')

    const hasLefties = Boolean(lefties.length)
    const hasRighties = Boolean(righties.length)

    // Hide/Show Lefties
    $wrapLefty.style('display', hasLefties && isSplit ? 'block' : 'none')
    $wrapRighty.style('display', hasRighties && isSplit ? 'block' : 'none')
    $wrapAll.style('display', !isSplit ? 'block' : 'none')

    // Resize Left and Right Wrapper
    const leftyRows = Math.ceil(lefties.length / halfDims.cols)
    $wrapLeftyWrap.style('height', `${halfDims.rowHeight * leftyRows}px`)
    const rightyRows = Math.ceil(righties.length / halfDims.cols)
    $wrapRightyWrap.style('height', `${halfDims.rowHeight * rightyRows}px`)
    const allRows = Math.ceil(sortedData.length / wholeDims.cols)
    $wrapAll.style('height', `${wholeDims.rowHeight * allRows}px`)

    // Redraw grids
    if (isSplit) {
        orderGrid(lefties, $wrapLeftyWrap, isAnimation)
        orderGrid(righties, $wrapRightyWrap, isAnimation)
    } else {
        // Not split
        return orderGrid(sortedData, $wrapAll, true, isAnimation)
    }
}

async function orderGrid(
    sortedData,
    $wrapper,
    isAll = false,
    isAnimation = false
) {
    const suffix = isAll ? '-all' : ''
    const fullClass = svgClass + suffix

    const { cols, cardMargin, colWidth, cardWidth, cardHeight, rowHeight } =
        isAll ? wholeDims : halfDims
    const selection = $wrapper
        .selectAll(`svg.${fullClass}`)
        .data(sortedData, (d) => getSvgElementId(d, suffix))
        .join(
            (enter) => {
                const svg = enter
                    .append('svg')
                    .attr('class', (d) => `pitcher-arm-angle-${d.pitch_hand}`)
                    .attr('viewBox', `0 0 ${cardSVGWidth} ${cardSVGHeight}`)
                    .classed(fullClass, true)
                    .style('position', 'absolute')
                    .style('background', 'white')
                    .style('top', (d, i) =>
                        isAnimation
                            ? 0
                            : `${Math.floor(i / cols) * rowHeight}px`
                    )
                    .style('left', (d, i) =>
                        isAnimation
                            ? 0
                            : `${(i % cols) * colWidth + cardMargin / 2}px`
                    )
                    .attr('id', (d) => getSvgElementId(d, suffix))
                    .attr('width', cardWidth)
                    .attr('height', cardHeight)
                    .style('margin', `${cardMargin / 2}px`)

                svg.each((row) => createVisual(row, suffix, sortedData))
                return svg
            },
            (update) => {
                update
                    .attr('width', cardWidth)
                    .attr('height', cardHeight)
                    .style('top', (d, i) =>
                        isAnimation
                            ? 0
                            : `${Math.floor(i / cols) * rowHeight}px`
                    )
                    .style('left', (d, i) =>
                        isAnimation
                            ? 0
                            : `${(i % cols) * colWidth + cardMargin / 2}px`
                    )
                    .attr('opacity', 0)

                update.select('.mlb-team-logo').attr('x', getTeamLogoX)
                return update
            }
        )

    if (!isAnimation) {
        return selection
            .transition()
            .duration(400)
            .delay((d, i) => Math.min(i * 50, 2500))
            .attr('opacity', 1)
            .end()
    }

    await wait(200)
    const timing = document.getElementById('ddlTiming').value

    const totalAnimationTime = timing.includes('per')
        ? Number(timing.replace('per', '')) * sortedData.length * 1000
        : timing * 1000

    const flipTime = totalAnimationTime / sortedData.length
    return selection
        .attr('opacity', 1)
        .style('z-index', (d, i) => data.length - i)
        .transition()
        .duration(flipTime / 5)
        .delay((d, i) => (i + 1) * flipTime)
        .attr('opacity', 0)
        .end()
}

document.getElementById('ddlSort').addEventListener('change', (e) => {
    const newSortParam = sortingFns[e.target.value]
        ? e.target.value
        : 'ascending'
    const sortFn = sortingFns[newSortParam]
    isSplit = !newSortParam.includes('combo_')
    data.sort(sortFn)
    updateGrid(filteredData)

    // Update URL
    currentParams.set('sort', e.target.value)

    const url = `${window.location.pathname}?${currentParams.toString()}`
    window.history.replaceState('', '', url)
})

document.getElementById('ddlSize').addEventListener('change', (e) => {
    sizeParam = e.target.value
    halfDims = getDims($wrapLefty)
    wholeDims = getDims($wrapAll)

    updateGrid(filteredData)

    // Update URL
    currentParams.set('size', e.target.value)

    const url = `${window.location.pathname}?${currentParams.toString()}`
    window.history.replaceState('', '', url)
})

document.getElementById('ddlPerspective').addEventListener('change', (e) => {
    perspectiveParam = e.target.value

    Object.keys(lilGuys).forEach((key) => {
        lilGuys[key].flip()
    })

    updateGrid(filteredData)

    // Top Viz Update
    updateDims()
    updateChart()

    // Update URL
    currentParams.set('perspective', e.target.value)

    const url = `${window.location.pathname}?${currentParams.toString()}`
    window.history.replaceState('', '', url)
})

function populateUrl() {
    const parameters = {
        batSide: document.getElementById('ddlBatSide').value,
        dateStart: document.getElementById('ddlDateStart').value,
        dateEnd: document.getElementById('ddlDateEnd').value,
        gameType: getMultiSelectValues(gameTypeModal).join('|'),
        groupBy: getMultiSelectValues(groupByModal).slice(0, 4).join('|'),
        min: document.getElementById('ddlMin').value,
        minGroupPitches: document.getElementById('ddlMinGroupPitches').value,
        perspective: document.getElementById('ddlPerspective').value,
        pitchHand: document.getElementById('ddlPitchHand').value,
        pitchType: getMultiSelectValues(pitchTypeModal).join('|'),
        season: getMultiSelectValues(seasonModal).join('|'),
        size: document.getElementById('ddlSize').value,
        sort: document.getElementById('ddlSort').value,
        team: getMultiSelectValues(teamModal).join('|'),
    }

    const urlParams = new URLSearchParams(parameters)

    return `${window.location.pathname}?${urlParams.toString()}`
}

document.getElementById('btn-update').addEventListener('click', () => {
    window.location = populateUrl()
})

document.getElementById('btn-csv').addEventListener('click', () => {
    window.location.href = window.location.search
        ? `${window.location.href}&csv=true`
        : `${window.location.href}?csv=true`
})

document.getElementById('btn-animate').addEventListener('click', async () => {
    const oldIsSplit = isSplit
    isSplit = false
    await updateGrid(filteredData, true)
    await wait(1000)

    isSplit = oldIsSplit
    updateGrid(filteredData, false)
})

// VIZ HACK

const svgWrap = d3.select('#pitcher-arm-angle-viz-wrapper')
const originalWrapperDims = svgWrap.node().getBoundingClientRect()

const svg = d3.select('svg#pitcher-arm-angle-viz')

const originalSvgDims = svg.node().getBoundingClientRect()

// Columns on outside of SVG will hold images
const columnSize = (originalWrapperDims.width - originalSvgDims.width) / 2

const margin = 40

let width
let height
let xScale
let yScale
let tooltipHeight

const largeMargins = {
    top: 10,
    right: 60,
    bottom: 60,
    left: 60,
}

const smallCenteredMargins = {
    top: 10,
    right: 25,
    bottom: 50,
    left: 25,
}

const smallRelativeMargins = {
    left: 40,
    top: 10,
    right: 10,
    bottom: 50,
}

let margins = { ...largeMargins }

const wrap = svg.append('g')

const pitcherPerspective = wrap
    .append('text')
    .text(
        perspectiveParam === 'front'
            ? 'catcher perspective'
            : 'pitcher perspective'
    )

updateDims()

wrap.attr('transform', `translate(${margins.left}, ${margins.top})`)

const nonCenteredG = wrap.append('g').classed('non-centered-wrapper', true)

nonCenteredG
    .append('text')
    .classed('x-axis-title', true)
    .attr('font-size', 12)
    .attr('font-weight', 'bold')
    .style('text-transform', 'uppercase')
    .text('Feet From Center of Mass')
    .attr('y', height + 35)
    .attr('x', width / 2)
    .attr('text-anchor', 'middle')

nonCenteredG
    .append('text')
    .classed('y-axis-title', true)
    .attr('font-size', 12)
    .attr('font-weight', 'bold')
    .style('text-transform', 'uppercase')
    .text('Feet From Ground')
    .attr('text-anchor', 'middle')
    .attr('transform', `translate(-25, ${height / 2}) rotate(-90)`)

wrap.append('rect')
    .classed('background-rect', true)
    .attr('width', width)
    .attr('height', height)
    .attr('fill', 'rgba(255,255,255,0.3)')
    .attr('stroke', '#e1e1e1')

const circleRadius = width / 2 - 60

const anglePts = [
    -60, -50, -40, -30, -20, -10, 0, 10, 20, 30, 40, 50, 60, 120, 130, 140, 150,
    160, 170, 180, 190, 200, 210, 220, 230, 240,
]

const centeredG = wrap.append('g').classed('centered-wrapper', true)

centeredG
    .append('circle')
    .classed('centered-circle', true)
    .attr('r', circleRadius)
    .attr('cx', width / 2)
    .attr('cy', height / 2)
    .attr('fill', 'rgba(255,255,255,0.3)')
    .attr('stroke', 'white')
    .attr('stroke-width', 4)

const path = d3
    .line()
    .x(([x]) => xScale(x))
    .y(([, y]) => yScale(y))
    .curve(d3.curveBasis)

// Mound
wrap.append('g')
    .append('path')
    .classed('viz-mound', true)
    .attr(
        'd',
        path([
            [4, 0],
            [0, 0.8],
            [-4, 0],
        ])
    )
    .attr('fill', '#eee')
    .attr('stroke', '#d4d4d4')
    .attr('stroke-width', 1.5)

pitcherPerspective
    .attr('x', width / 2)
    .attr('y', height - 10)
    .attr('text-anchor', 'middle')
    .attr('fill', '#666')
    .style('text-transform', 'uppercase')
    .style('font-size', '10px')
    .raise()

// Legend
const defaultBallRadius = 6
const ballStroke = 1.5
const defaultRectWidth = 12

const legend = wrap
    .append('g')
    .classed('legend', true)
    .attr('font-weight', 'bold')
    .attr('transform', `translate(20, ${height - 60})`)

const ballLegend = legend.append('g')
const shoulderLegend = legend.append('g').attr('transform', 'translate(0, 20)')

ballLegend
    .append('circle')
    .attr('r', defaultBallRadius)
    .attr('fill', 'none')
    .attr('stroke', '#666')
    .attr('stroke-width', ballStroke)

ballLegend
    .append('text')
    .text('Ball')
    .attr('dominant-baseline', 'middle')
    .attr('x', 10)
    .attr('y', 1)

shoulderLegend
    .append('rect')
    .attr('width', defaultRectWidth)
    .attr('height', defaultRectWidth)
    .attr('fill', 'none')
    .attr('stroke', '#666')
    .attr('stroke-width', ballStroke)
    .attr('x', defaultRectWidth / -2)
    .attr('y', defaultRectWidth / -2)
shoulderLegend
    .append('text')
    .text('Shoulder')
    .attr('dominant-baseline', 'middle')
    .attr('x', 10)
    .attr('y', 1)

const $disclaimer = wrap
    .append('text')
    .text(truncated ? `Showing the top ${vizLowerBound} results` : '')
    .attr('x', width - 10)
    .attr('y', 20)
    .attr('text-anchor', 'end')
    .style('font-style', 'italic')

function updateDims() {
    const svgDims = svg.node().getBoundingClientRect()

    if (svgDims.width >= 600) {
        margins = largeMargins
    } else if (centered) {
        margins = smallCenteredMargins
    } else {
        margins = smallRelativeMargins
    }

    const dim = svgDims.width - margins.left - margins.right

    height = Math.max(200, dim)
    width = Math.max(200, dim)

    tooltipHeight = height * (6.5 / 8)
    const svgHeight = dim + margins.top + margins.bottom

    svg.attr('height', svgHeight)

    const xDomain = perspectiveParam === 'front' ? [-4, 4] : [4, -4]

    xScale = d3.scaleLinear().domain(xDomain).range([0, width])
    yScale = d3.scaleLinear().domain([8, 0]).range([0, height])

    pitcherPerspective.text(
        perspectiveParam === 'front'
            ? 'catcher perspective'
            : 'pitcher perspective'
    )
}

window.addEventListener(
    'resize',
    debounce(() => {
        updateDims()
        updateChart()
    }, 200)
)

function updateAxes() {
    wrap.select('.background-rect')
        .transition()
        .attr('width', width)
        .attr('height', height)

    wrap.select('g.legend')
        .transition()
        .attr('transform', `translate(20, ${height - 60})`)

    nonCenteredG.attr('opacity', centered ? 0 : 1)

    nonCenteredG
        .select('.x-axis-title')
        .transition()
        .attr('x', width / 2)
        .attr('y', height + 35)

    nonCenteredG
        .select('.y-axis-title')
        .transition()
        .attr('transform', `translate(-25, ${height / 2}) rotate(-90)`)

    // Horizontal Ticks
    nonCenteredG
        .selectAll('line.horizontal-tick')
        .data(yScale.ticks(), (d) => d)
        .join(
            (enter) =>
                enter
                    .append('line')
                    .classed('horizontal-tick', true)
                    .attr('x1', 0)
                    .attr('y1', (d) => yScale(d))
                    .attr('x2', width)
                    .attr('y2', (d) => yScale(d))
                    .attr('stroke', 'white'),
            (update) => {
                update
                    .transition()
                    .attr('y1', (d) => yScale(d))
                    .attr('x2', width)
                    .attr('y2', (d) => yScale(d))

                return update
            }
        )

    // Vertical Ticks
    nonCenteredG
        .selectAll('line.vertical-tick')
        .data(xScale.ticks(), (d) => d)
        .join(
            (enter) =>
                enter
                    .append('line')
                    .classed('vertical-tick', true)
                    .attr('x1', (d) => xScale(d))
                    .attr('y1', 0)
                    .attr('x2', (d) => xScale(d))
                    .attr('y2', height)
                    .attr('stroke', 'white')
                    .attr('stroke-width', (d) => (d === 0 ? 4 : 1)),
            (update) => {
                update
                    .transition()
                    .attr('x1', (d) => xScale(d))
                    .attr('x2', (d) => xScale(d))
                    .attr('y2', height)

                return update
            }
        )

    // Mound
    const moundPath = d3
        .line()
        .x(([x]) => xScale(x))
        .y(([, y]) => yScale(y))
        .curve(d3.curveBasis)

    // Mound
    wrap.select('.viz-mound')
        .transition()
        .attr(
            'd',
            moundPath([
                [4, 0],
                [0, 0.8],
                [-4, 0],
            ])
        )

    pitcherPerspective
        .transition()
        .attr('x', width / 2)
        .attr('y', height - 10)

    const xAxis = d3
        .axisBottom()
        .scale(xScale)
        .tickFormat((d) => Math.abs(d))

    nonCenteredG
        .select('.x-axis')
        .attr('transform', `translate(0, ${height})`)
        .call(xAxis)

    const yAxis = d3.axisLeft().scale(yScale)

    nonCenteredG.select('.y-axis').call(yAxis)

    // Centered
    centeredG.attr('opacity', centered ? 1 : 0)

    // eslint-disable-next-line no-shadow
    const circleRadius = width / 2 - 60

    centeredG
        .select('circle.centered-circle')
        .transition()
        .attr('r', circleRadius)
        .attr('cx', width / 2)
        .attr('cy', height / 2)

    centeredG
        .selectAll('g.angle-legend')
        .data(anglePts)
        .join(
            (enter) => {
                const el = enter
                    .append('g')
                    .classed('angle-legend', true)
                    .attr('transform', (d) => {
                        const initX = width / 2
                        const initY = height / 2

                        const radians = (d * Math.PI) / 180
                        const x =
                            initX + Math.cos(radians) * (circleRadius + 20)
                        const y =
                            initY - Math.sin(radians) * (circleRadius + 20)

                        return `translate(${x}, ${y})`
                    })

                el.append('text')
                    .text((d) => {
                        if (d % 30 === 0) {
                            return `${d > 90 ? 180 - d : d}°`
                        }
                        return ''
                    })
                    .attr('text-anchor', (d) => (d > 90 ? 'end' : 'start'))
                    .attr('dominant-baseline', 'middle')
                    .attr('font-weight', 'bold')

                return el
            },
            (update) => {
                update.attr('transform', (d) => {
                    const initX = width / 2
                    const initY = height / 2

                    const radians = (d * Math.PI) / 180
                    const x = initX + Math.cos(radians) * (circleRadius + 20)
                    const y = initY - Math.sin(radians) * (circleRadius + 20)

                    return `translate(${x}, ${y})`
                })
                return update
            }
        )
    centeredG
        .selectAll('g.angle-ticks')
        .data(anglePts)
        .join(
            (enter) => {
                const el = enter
                    .append('g')
                    .classed('angle-ticks', true)
                    .attr('transform', (d) => {
                        const initX = width / 2
                        const initY = height / 2

                        return `translate(${initX}, ${initY}) rotate(${-d})`
                    })
                el.append('line')
                    .classed('angle-ticks-tick', true)
                    .attr('x1', (d) =>
                        d % 30 === 0 ? circleRadius - 10 : circleRadius - 5
                    )
                    .attr('y1', 0)
                    .attr('x2', (d) =>
                        d % 30 === 0 ? circleRadius + 10 : circleRadius + 5
                    )
                    .attr('y1', 0)
                    .attr('stroke', 'black')
                    .attr('stroke-width', (d) => (d % 30 === 0 ? 5 : 2))

                return el
            },
            (update) => {
                update.attr('transform', (d) => {
                    const initX = width / 2
                    const initY = height / 2

                    return `translate(${initX}, ${initY}) rotate(${-d})`
                })

                update
                    .select('line.angle-ticks-tick')

                    .attr('x1', (d) =>
                        d % 30 === 0 ? circleRadius - 10 : circleRadius - 5
                    )
                    .attr('x2', (d) =>
                        d % 30 === 0 ? circleRadius + 10 : circleRadius + 5
                    )

                return update
            }
        )
}

updateAxes()

const baseStrokeWidth = 2

const wrapwrap = wrap.append('g')
let viz1Gs
let lineGs

const shoulderRadiusScale = d3
    .scaleLinear()
    .domain([400, 680])
    .range([0.04, 0.065])
    .clamp(true)

// A subtle-ish arm
function getArmPoints(row) {
    const radiusShoulder = shoulderRadiusScale(width)
    const radiusBall = radiusShoulder / 2.8

    const shoulderX = row.relative_shoulder_x
    const shoulderY = row.shoulder_z
    const ballX = row.relative_release_ball_x
    const ballY = row.release_ball_z

    const rad1 = row.radians + Math.PI / 2
    const deltaX1 = Math.cos(rad1)
    const deltaY1 = Math.sin(rad1)

    // x is swapped because of scales
    const x1 = shoulderX - deltaX1 * radiusShoulder
    const y1 = shoulderY + deltaY1 * radiusShoulder
    const x1Ball = ballX - deltaX1 * radiusBall
    const y1Ball = ballY + deltaY1 * radiusBall

    const rad2 = row.radians - Math.PI / 2
    const deltaX2 = Math.cos(rad2)
    const deltaY2 = Math.sin(rad2)

    // x is swapped because of scales
    const x2 = shoulderX - deltaX2 * radiusShoulder
    const y2 = shoulderY + deltaY2 * radiusShoulder
    const x2Ball = ballX - deltaX2 * radiusBall
    const y2Ball = ballY + deltaY2 * radiusBall

    return [
        [x1Ball, y1Ball],
        [x1, y1],
        [x2, y2],
        [x2Ball, y2Ball],
    ]
}

function fillCircle(d) {
    if (isGroupByPitchType) {
        return getPitchTypeColor(d.api_pitch_type_group03)
    }

    return d.pitch_hand === 'L' ? 'var(--lefty-color)' : 'var(--righty-color)'
}

function strokeCircle(d) {
    if (isGroupByPitchType) {
        return '#fff'
    }

    return d.pitch_hand === 'L'
        ? 'var(--lefty-darker-color)'
        : 'var(--righty-darker-color)'
}

const voronoiWrap = wrap.append('g')

const toggle = wrap
    .append('g')
    .classed('toggle', true)
    // Meh
    .attr('transform', `translate(${width * 0.9}, ${height - 68})`)

createToggle(
    toggle,
    (isRelative) => {
        centered = !isRelative
        updateDims()
        updateChart()
    },
    {
        text1Bottom: 'RELATIVE',
        text2Top: 'CENTERED',
        text2Bottom: 'ON SHOULDER',
    }
)

const ballRadiusScale = d3
    .scaleLinear()
    .domain([400, 680])
    .range([4, 6])
    .clamp(true)

const blueish = d3.color('#4591a2')
const darkerBlueish = blueish.darker(0.4)

// eslint-disable-next-line no-shadow
function updateVizChart(data) {
    const selected = new Set(filteredData.map((d) => d.unique_id))
    const isFiltered = filteredData.length !== data.length

    const defaultArmOpacity = 0.14
    const defaultOpacity = 0.6

    const defaultRingOpacity = isFiltered ? 0.3 : 1

    const scaledPath = d3
        .line()
        .x(([x]) => xScale(x))
        .y(([, y]) => yScale(y))

    const lineVsArm = false

    const ballRadius = ballRadiusScale(width)
    const rectWidth = 2 * ballRadius
    const emphasizedBallSize = ballRadius * 2
    const emphasizedRectWidth = 1.5 * rectWidth

    lineGs = wrapwrap
        .selectAll('g.viz-connections')
        .data(data, getArmId)
        .join(
            (enter) => {
                const g = enter
                    .append('g')
                    .classed('viz-connections', true)
                    .attr('opacity', getArmOpacity)
                    .attr('data-id', (d) => d.name)

                if (lineVsArm) {
                    g.append('line')
                        .classed('viz-pitcher-connection', true)
                        .attr('x1', (d) => xScale(d.relative_release_ball_x))
                        .attr('y1', (d) => yScale(d.release_ball_z))
                        .attr('x2', (d) => xScale(d.relative_shoulder_x))
                        .attr('y2', (d) => yScale(d.shoulder_z))
                        .attr('stroke', '#666')
                        .attr('stroke-width', baseStrokeWidth)
                } else {
                    g.append('path')
                        .classed('viz-pitcher-connection', true)
                        .attr('d', (row) => {
                            const points = getArmPoints(row)
                            const d = scaledPath(points)
                            return d
                        })
                        .attr('fill', '#666')
                        .attr('stroke', '#aaa')
                        .attr('stroke-width', 0.5)
                }
                return g
            },
            (update) => {
                // can't transition here because of how it's done later
                update.attr('opacity', getArmOpacity)

                if (lineVsArm) {
                    update
                        .select('line.viz-pitcher-connection')
                        .transition()
                        .attr('x1', (d) => xScale(d.relative_release_ball_x))
                        .attr('y1', (d) => yScale(d.release_ball_z))
                        .attr('x2', (d) => xScale(d.relative_shoulder_x))
                        .attr('y2', (d) => yScale(d.shoulder_z))
                } else {
                    update
                        .select('path.viz-pitcher-connection')
                        .transition()
                        .attr('d', (row) => {
                            const points = getArmPoints(row)
                            const d = scaledPath(points)
                            return d
                        })
                }

                return update
            }
        )
        .lower()

    function getOpacity(d) {
        if (currentData?.unique_id === d.unique_id) {
            return 1
        }

        if (currentData && isFiltered) {
            return selected.has(d.unique_id) ? 0.5 : 0.06
        }

        if (isFiltered) {
            return selected.has(d.unique_id) ? 0.9 : 0.06
        }

        return defaultOpacity
    }
    function getArmOpacity(d) {
        if (currentData?.unique_id === d.unique_id) {
            return 1
        }
        if (currentData && isFiltered) {
            return selected.has(d.unique_id) ? 0.5 : 0.02
        }

        if (isFiltered) {
            return selected.has(d.unique_id) ? 0.9 : 0.02
        }

        return defaultArmOpacity
    }
    function getRingOpacity(d) {
        if (!handSelectedKeys.includes(d.pitcher)) {
            return 0
        }

        if (currentData?.unique_id === d.unique_id) {
            return 1
        }

        if (currentData) {
            return defaultOpacity / 3
        }

        if (!selected.has(d.unique_id)) {
            return defaultRingOpacity / 3
        }

        return defaultRingOpacity
    }

    viz1Gs = wrapwrap
        .selectAll('g.viz-pitcher')
        .data(data, getArmId)
        .join(
            (enter) => {
                const g = enter
                    .append('g')
                    .classed('viz-pitcher', true)
                    .attr('data-id', (d) => d.name)
                    .attr('id', getArmIdAlt)

                const circleG = g
                    .append('g')
                    .classed('viz-circles', true)
                    .attr('transform', (d) => {
                        const x = xScale(d.relative_release_ball_x)
                        const y = yScale(d.release_ball_z)
                        return `translate(${x}, ${y})`
                    })

                circleG
                    .append('circle')
                    .classed('highlighted-back', true)
                    .attr('opacity', getRingOpacity)
                    .attr('r', ballRadius + 4)
                    .attr('stroke', (d) =>
                        d.pitch_hand === 'L'
                            ? 'var(--lefty-darker-color)'
                            : 'var(--righty-darker-color)'
                    )
                    .attr('stroke', 'rgba(0,0,0,0.1)')
                    .attr('stroke-width', 4)
                    .attr('fill', 'none')
                circleG
                    .append('circle')
                    .classed('highlighted', true)
                    .attr('opacity', getRingOpacity)
                    .attr('r', ballRadius + 4)
                    .attr('stroke', (d) =>
                        d.pitch_hand === 'L'
                            ? 'var(--lefty-darker-color)'
                            : 'var(--righty-darker-color)'
                    )
                    .attr('stroke-width', 4)
                    .attr('stroke-dasharray', '2 2')
                    .attr('fill', 'none')

                circleG
                    .append('circle')
                    .classed('viz-pitcher-ball', true)
                    .attr('r', (d) =>
                        currentData?.unique_id === d.unique_id
                            ? emphasizedBallSize
                            : ballRadius
                    )
                    .attr('fill', fillCircle)
                    .attr('stroke', strokeCircle)
                    .attr('stroke-width', ballStroke)
                    .attr('opacity', getOpacity)

                g.append('rect')
                    .classed('viz-pitcher-shoulder', true)
                    .attr('width', (d) =>
                        currentData?.unique_id === d.unique_id
                            ? emphasizedRectWidth
                            : rectWidth
                    )
                    .attr('height', (d) =>
                        currentData?.unique_id === d.unique_id
                            ? emphasizedRectWidth
                            : rectWidth
                    )
                    .attr('fill', (d) =>
                        currentData?.unique_id === d.unique_id
                            ? blueish
                            : 'white'
                    )
                    .attr('stroke-width', ballStroke)
                    .attr('stroke', darkerBlueish)
                    .attr('transform', (d) => {
                        const half =
                            currentData?.unique_id === d.unique_id
                                ? emphasizedRectWidth / 2
                                : rectWidth / 2
                        const x = xScale(d.relative_shoulder_x) - half
                        const y = yScale(d.shoulder_z) - half

                        // Just mess with things until it mostly works
                        const rise =
                            yScale(d.release_ball_z) - yScale(d.shoulder_z)
                        // swapped x axis
                        const run =
                            xScale(d.relative_shoulder_x) -
                            xScale(d.relative_release_ball_x)

                        const rads = Math.atan2(rise, run)
                        const degrees = (rads * 180) / Math.PI

                        return `translate(${x}, ${y}), rotate(${-degrees}, ${half}, ${half})`
                    })
                    .attr('opacity', getOpacity)

                return g
            },
            (update) => {
                update
                    .select('g.viz-circles')
                    .transition()
                    .attr('transform', (d) => {
                        const x = xScale(d.relative_release_ball_x)
                        const y = yScale(d.release_ball_z)
                        return `translate(${x}, ${y})`
                    })

                update
                    .select('circle.highlighted')
                    .attr('opacity', getRingOpacity)

                update
                    .select('circle.highlighted-back')
                    .attr('opacity', getRingOpacity)

                update
                    .select('circle.viz-pitcher-ball')
                    .attr('r', (d) =>
                        currentData?.unique_id === d.unique_id
                            ? emphasizedBallSize
                            : ballRadius
                    )
                    .attr('opacity', getOpacity)

                update
                    .select('rect.viz-pitcher-shoulder')
                    .attr('width', (d) =>
                        currentData?.unique_id === d.unique_id
                            ? emphasizedRectWidth
                            : rectWidth
                    )
                    .attr('height', (d) =>
                        currentData?.unique_id === d.unique_id
                            ? emphasizedRectWidth
                            : rectWidth
                    )
                    .attr('opacity', getOpacity)
                    .attr('fill', (d) =>
                        currentData?.unique_id === d.unique_id
                            ? blueish
                            : 'white'
                    )
                    .attr('transform', (d) => {
                        const half =
                            currentData?.unique_id === d.unique_id
                                ? emphasizedRectWidth / 2
                                : rectWidth / 2
                        const x = xScale(d.relative_shoulder_x) - half
                        const y = yScale(d.shoulder_z) - half

                        // Just mess with things until it mostly works
                        const rise =
                            yScale(d.release_ball_z) - yScale(d.shoulder_z)
                        // swapped x axis
                        const run =
                            xScale(d.relative_shoulder_x) -
                            xScale(d.relative_release_ball_x)

                        const rads = Math.atan2(rise, run)
                        const degrees = (rads * 180) / Math.PI

                        return `translate(${x}, ${y}), rotate(${-degrees}, ${half}, ${half})`
                    })

                return update
            }
        )
    // Do this after? transitions
    if (currentData) {
        showTooltip(currentData)

        lineGs.filter((d) => d.unique_id === currentData.unique_id).raise()
        viz1Gs.filter((d) => d.unique_id === currentData.unique_id).raise()
    } else {
        hideTooltip()
    }

    if (centered) {
        // Centers the shoulder
        lineGs.transition().attr('transform', (d) => {
            const shoulderX = xScale(d.relative_shoulder_x)
            const shoulderY = yScale(d.shoulder_z)
            const pushX = width / 2 - shoulderX
            const pushY = height / 2 - shoulderY
            return `translate(${pushX},${pushY})`
        })

        // Centers the shoulder
        viz1Gs.transition().attr('transform', (d) => {
            const shoulderX = xScale(d.relative_shoulder_x)
            const shoulderY = yScale(d.shoulder_z)
            const pushX = width / 2 - shoulderX
            const pushY = height / 2 - shoulderY
            return `translate(${pushX},${pushY})`
        })
    } else {
        lineGs.transition().attr('transform', `translate(0, 0)`)
        viz1Gs.transition().attr('transform', `translate(0, 0)`)
    }
}

const tooltipWrap = wrap
    .append('g')
    .attr('transform', `translate(${width / 2}, ${tooltipHeight})`)
    .attr('opacity', 0)

tooltipWrap
    .append('image')
    .attr('href', 'https://midfield.mlbstatic.com/v1/people/000000/spots/60')
    .attr('width', 60)
    .attr('x', -30)
    .attr('y', -105)

const tooltipImageWidth = 60
const tooltipImageHeight = 60
const tooltipImage = tooltipWrap
    .append('image')
    .attr('width', tooltipImageWidth)
    .attr('x', tooltipImageWidth / -2)
    .attr('y', -tooltipImageHeight - 45)

const tooltipTitle1 = tooltipWrap
    .append('text')
    .attr('font-size', 20)
    .attr('font-weight', 'bold')
    .attr('text-anchor', 'middle')
    .attr('y', -20)

const tooltipSubTitle1 = tooltipWrap
    .append('text')
    .attr('font-size', 14)
    .attr('text-anchor', 'middle')

const tooltipSubTitle2 = tooltipWrap
    .append('text')
    .attr('font-size', 14)
    .attr('text-anchor', 'middle')
    .attr('y', 18)

const $picWrap = document.getElementById('arm-angle-selected-pic')
const $pic = $picWrap.querySelector('img')

function getGroupByVals(row, groupBys = serverParams.secondaryGroupBy) {
    return groupBys
        .filter((d) => d !== 'api_game_date_month_mm')
        .map((prop) => {
            const val = row[prop]

            if (prop.includes('api_pitch_type')) {
                return getShortPitchName(val)
            }

            if (prop === 'bat_side') {
                return `Bats: ${val}`
            }

            if (prop === 'fld_team_id') {
                return row.team_name
            }

            if (prop === 'game_type') {
                return GAME_TYPE_NAMES[val] || ''
            }

            return val
        })
}

function hideTooltip() {
    // lazy
    if (!tooltipWrap) {
        return
    }

    tooltipWrap.attr('opacity', 0)
    tooltipTitle1.text('')
    tooltipSubTitle1.text('')
    tooltipSubTitle2.text('')

    $picWrap.style.display = 'none'
}

function showTooltip(row) {
    tooltipWrap.attr('opacity', 1)

    const currentHref = tooltipImage.attr('href')
    const futureHref = `https://midfield.mlbstatic.com/v1/people/${row.pitcher}/spots/60`

    if (currentHref !== futureHref) {
        tooltipImage
            // Don't fully understand why this isn't called a million times
            .on('load', function handleLoad() {
                d3.select(this).attr('opacity', 1)
            })
            .attr('opacity', 0)
            .attr('href', futureHref)
    }

    tooltipTitle1.text(row.name)
    tooltipSubTitle1.text(
        `${row.pitch_hand}HP // ${guff(row.arm_angle, 'arm_angle_viz')}°`
    )

    const groupByVals = getGroupByVals(row).join(` ${DOT} `)
    tooltipSubTitle2.text(groupByVals)

    // Show extra image
    if (handSelectedKeys.includes(row.pitcher)) {
        $picWrap.style.display = 'block'

        const y = yScale(row.release_ball_z)
        const top = Math.max(20, y + margin - columnSize / 2)
        $picWrap.style.top = `${top}px`
        $pic.width = columnSize
        $pic.height = columnSize
        $pic.style.border = '1.5px solid #666'
        $pic.style.background = 'rgb(255, 255, 255, 0.3)'

        if (row.pitch_hand === 'L') {
            $picWrap.style.left = `15px`
            $picWrap.style.right = 'initial'
        } else {
            $picWrap.style.left = `initial`
            $picWrap.style.right = '25px'
        }

        $pic.src = getImageUrl(handSelectedTooltips[row.pitcher])
    } else {
        $picWrap.style.display = 'none'
    }
}

function getVoronoiPoints() {
    const releasePts = filteredData.map((row) => ({
        x: row.relative_release_ball_x,
        y: row.release_ball_z,
        xCenter:
            xScale(row.relative_release_ball_x) +
            width / 2 -
            xScale(row.relative_shoulder_x),
        yCenter:
            yScale(row.release_ball_z) + height / 2 - yScale(row.shoulder_z),
        ...row,
    }))
    const shoulderPts = filteredData.map((row) => ({
        x: row.relative_shoulder_x,
        y: row.shoulder_z,
        ...row,
    }))
    // I wonder if this will be bad for performance eventually...
    const avgPts = filteredData.map((row) => ({
        x: (row.relative_release_ball_x + row.relative_shoulder_x) / 2,
        y: (row.release_ball_z + row.shoulder_z) / 2,
        ...row,
    }))

    const points = [...releasePts, ...shoulderPts, ...avgPts]
    return points.map((row) => ({
        ...row,
        xCenter: xScale(row.x) + width / 2 - xScale(row.relative_shoulder_x),
        yCenter: yScale(row.y) + height / 2 - yScale(row.shoulder_z),
    }))
}

let mouseout

function updateVoronoi() {
    const [propX, propY] = centered ? ['xCenter', 'yCenter'] : ['x', 'y']

    const voronoiPts = getVoronoiPoints(filteredData)

    if (!voronoiPts.length) {
        voronoiWrap.selectAll('path.voronoi').remove()
        return
    }

    const delaunay = d3.Delaunay.from(
        voronoiPts,
        (d) => (centered ? d[propX] : xScale(d[propX])),
        (d) => (centered ? d[propY] : yScale(d[propY]))
    )
    const [minX, maxX] = d3.extent(voronoiPts, (d) =>
        centered ? d[propX] : xScale(d[propX])
    )
    const [minY, maxY] = d3.extent(voronoiPts, (d) =>
        centered ? d[propY] : yScale(d[propY])
    )

    const voronoi1 = delaunay.voronoi([
        Math.max(0, minX - 10),
        Math.max(0, minY - 10),
        Math.min(maxX + 10, width),
        Math.min(maxY + 10, height),
    ])

    voronoiWrap
        .selectAll('path.voronoi')
        .data(voronoiPts)
        .join(
            (enter) =>
                enter
                    .append('path')
                    .classed('voronoi', true)
                    .attr('d', (d, i) => voronoi1.renderCell(i))
                    .attr('fill', 'none')
                    .attr('stroke', 'none')
                    .attr('pointer-events', 'all')
                    .on('mouseover', (e, d) => {
                        mouseout = false

                        if (currentData?.unique_id === d.unique_id) {
                            // I think this is still worth the short circuit
                            // if we are still on the same player we want the $pic.src to be reset
                            // But this way we don't have to call updateVizChart, which I _assume_ to be expensive.
                            showTooltip(d)
                            return
                        }

                        currentData = d
                        updateVizChart(data)
                    })
                    .on('mouseout', () => {
                        mouseout = true
                        if (filteredData.length !== 1) {
                            $pic.src = ''
                        }
                        setTimeout(() => {
                            if (mouseout && filteredData.length !== 1) {
                                currentData = null
                                updateVizChart(data)
                            }
                        }, 50)
                    }),
            (update) => {
                update.attr('d', (d, i) => voronoi1.renderCell(i))
                return update
            }
        )
}

const xAxis = d3
    .axisBottom()
    .scale(xScale)
    .tickFormat((d) => Math.abs(d))

nonCenteredG
    .append('g')
    .attr('class', 'axis x-axis')
    .attr('transform', `translate(0, ${height})`)
    .call(xAxis)

const yAxis = d3.axisLeft().scale(yScale)
nonCenteredG.append('g').attr('class', 'axis y-axis').call(yAxis)

function getArmId(row) {
    return row.unique_id
}
function getArmIdAlt(row) {
    return `${row.unique_id}-1`
}

const $input = false

function getFilteredData() {
    const search = $input.value

    if (!search.trim()) {
        return data
    }

    const normalized_search = normalizeAccents(search)

    return data.filter(
        (d) =>
            d.normal_name_display_last_first.includes(normalized_search) ||
            d.normal_name_display_first_last.includes(normalized_search) ||
            d.normal_short_name.includes(normalized_search) ||
            Number(d.pitcher) === Number(normalized_search)
    )
}

if (data && data.length) {
    if ($input) {
        $input.addEventListener('input', () => {
            const newData = getFilteredData()
            const newDataJson = JSON.stringify(newData)

            if (newDataJson !== filteredDataJson) {
                filteredData = newData
                filteredDataJson = newDataJson
                updateGrid(filteredData)

                if (filteredData.length === 1) {
                    // eslint-disable-next-line prefer-destructuring
                    currentData = filteredData[0]
                } else {
                    currentData = null
                }

                updateVizChart(data)

                updateVoronoi()
            }
        })
    }
}

function updateChart() {
    // Update Rect
    wrap.transition().attr(
        'transform',
        `translate(${margins.left}, ${margins.top})`
    )
    // Update Title
    const title = centered
        ? 'Arm Angle at Release Point'
        : 'Shoulder and Ball Location at Release Point'
    $title.innerText = title

    $disclaimer.attr('x', width - 10)

    // Update Axes
    updateAxes()

    // Update Scatter
    updateVizChart(data)

    // Update Voronoi
    updateVoronoi()

    // Update tooltip
    tooltipWrap.attr('transform', `translate(${width / 2}, ${tooltipHeight})`)

    // Update Toggle
    toggle
        .transition()
        .attr('transform', `translate(${width * 0.9}, ${height - 68})`)
}

function handleFilterData(pitcherIdArr) {
    const pitcherIds = new Set(pitcherIdArr.map(Number))

    if (!pitcherIds.size) {
        data = allData.filter((d) => d.n_pitches >= threshold)
        filteredData = data
        filteredVizData = vizData
    } else {
        filteredData = allData.filter((d) => pitcherIds.has(d.pitcher))
        data = allData.filter(
            (d) => d.n_pitches >= threshold || pitcherIds.has(d.pitcher)
        )
        filteredVizData = vizData.filter((d) => pitcherIds.has(d.pitcher))
    }

    if (filteredVizData.length === 1) {
        // eslint-disable-next-line prefer-destructuring
        currentData = filteredData[0]
    } else {
        currentData = null
    }
}

let showTable = currentParams.get('view') === 'table'

const $lilGuyFilters = document.querySelectorAll('.little-guy-viz-filter')

function toggleLittleGuyVizFilters(showFilters) {
    $lilGuyFilters.forEach((el) => {
        // eslint-disable-next-line no-param-reassign
        el.style.display = showFilters ? '' : 'none'
    })
}

const $leagueAverageToggle = document.getElementById(
    'btn-toggle-league-average'
)

function toggleTable() {
    if (showTable) {
        // Hide viz + filters
        $wrap.style('display', 'none')
        toggleLittleGuyVizFilters(false)

        // Show table
        $tableDiv.style.display = ''

        // Load table
        loadPitcherArmAngleTable(allData, showLeagueAverage)
        $leagueAverageToggle?.classList.remove('hidden')
    } else {
        // Hide table
        $tableDiv.style.display = 'none'

        // Show viz + filters
        $wrap.style('display', '')
        toggleLittleGuyVizFilters(true)

        // Load viz
        updateGrid(filteredData)
        $leagueAverageToggle?.classList.add('hidden')
    }
}

const initFilterStr = currentParams.get('filter')
const initFilterArr = initFilterStr ? initFilterStr.split('|') : []

handleFilterData(initFilterArr)

updateVizChart(data)
updateVoronoi()

toggleTable()

document.getElementById('ddlView').addEventListener('change', (e) => {
    showTable = e.target.value === 'table'

    toggleTable()

    // Update URL
    currentParams.set('view', e.target.value)

    const url = `${window.location.pathname}?${currentParams.toString()}`
    window.history.replaceState('', '', url)
})

$leagueAverageToggle?.addEventListener('click', () => {
    showLeagueAverage = !showLeagueAverage
    const latestParams = new URLSearchParams(window.location.search)
    loadPitcherArmAngleTable(
        data,
        showLeagueAverage,
        latestParams.get('sortColumn'),
        latestParams.get('sortDirection')
    )
})
